<template>
  <div class="balance">
    <div v-if="isSideVisible" class="balance__side pretty-scrollbar">
      <button
        v-for="(item, i) in accounts"
        :key="`balance__account_${i}`"
        class="balance__account balance__card"
        :class="{ balance__account_active: item === currentAccount }"
        @click="pickItem($event, item)"
      >
        <b class="balance__code">{{ item.code }}</b>
        <span class="balance__address">{{ item.address }}</span>
        <span
          class="balance__debit sum"
          :class="{
            sum_negative: item.debit < 0,
            sum_positive: item.debit > 0,
          }"
        >
          {{ item.debit | sum }}
        </span>
      </button>
    </div>
    <div class="balance__main">
      <BaseNote
        v-if="hasCharges"
        v-bind="note"
        class="balance__note"
      />
      <div class="balance__content balance__card">
        <div
          v-if="hasCharges"
          ref="container"
          class="balance__items pretty-scrollbar"
        >
          <Charges
            :items="charges"
            :sum="sum"
            :diff-sum="diffSum"
            class="balance__item"
            @pay="$emit('pay')"
          />
        </div>
        <div
          v-else
          class="balance__empty"
        >
          <BaseAlert v-bind="empty" />
        </div>
        <BaseSpinner
          v-if="isSpinnerVisible"
          :size="48"
          :stroke="4"
          class="balance__spinner"
        />
      </div>
    </div>
  </div>
</template>

<script>
import BaseAlert from '@/components/BaseAlert.vue';
import BaseNote from '@/components/BaseNote.vue';
import Charges from '@/components/Charges.vue';
import BaseSpinner from '@/components/BaseSpinner.vue';

export default {
  name: 'MyBalance',
  components: {
    BaseAlert,
    BaseNote,
    Charges,
    BaseSpinner,
  },
  props: {
    accounts: {
      type: Array,
      default: () => [],
    },
    charges: {
      type: Array,
      default: () => [],
    },
    sum: {
      type: Number,
      default: 0,
    },
    diffSum: {
      type: Number,
      default: 0,
    },
    currentAccount: {
      type: Object,
      default: null,
    },
    isSideVisible: {
      type: Boolean,
      default: true,
    },
    isSpinnerVisible: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    note() {
      return {
        description: {
          text: this.$t('texts.sum'),
        },
      };
    },
    hasCharges() {
      return Boolean(this.charges.length);
    },
    empty() {
      return {
        icon: {
          name: 'important',
          theme: 'secondary',
          size: 'm',
        },
        title: {
          text: this.$t('texts.noCharges'),
          size: 'm',
        },
        actions: [],
      };
    },
  },
  methods: {
    pickItem(e, item) {
      this.$nextTick(() => {
        this.scrollContainerToTop();
        this.scrollElementIntoView(e.target);
      });
      this.$emit('pick', item);
    },
    scrollElementIntoView(el) {
      if (!el) return;
      el.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    },
    scrollContainerToTop() {
      const { container } = this.$refs;
      if (container) {
        container.scrollTop = 0;
      }
    },
  },
};
</script>

<style lang="stylus">
.balance
  display flex
  width 100%
  height 100%

  $balance_side_padding = 1.5em

  &__main
  &__side
    flex-grow 1
    flex-basis 0
    max-width 100%

  &__side
    max-width 27%
    padding-right 'calc(0.7em - %s)' % ($scroll_width * 1px)
    padding-bottom 1em
    &::-webkit-scrollbar-thumb
      background-color transparent

  &__main
    display flex
    flex-direction column
    max-width 100%
    height 100%
    ~/__side ~ &
      max-width 73%

  &__card
    background-color #fff
    box-shadow 0 0.6em 1em -0.5em alpha($c_primary, 20%)
    border-radius 0.25em
    padding $balance_side_padding

  &__account
    position relative
    display block
    width 100%
    padding-top 1.4em
    padding-bottom @padding-top
    margin 0
    border 0
    text-align left
    margin-top 0.7em
    &:first-child
      margin-top 0
    &:before
      content ''
      abs top left right bottom
      box-shadow inset 0 0 0 0.15em $c_primary
      border-radius inherit
      transition opacity 250ms
      opacity 0
    &_active
      &:before
        opacity 1

  &__code
    display block
    font-size 1.15em
    font-weight 400
    color $c_primary

  &__address
    display block
    font-size 0.85em
    line-height 1.4
    color $c_text_dim
    margin-top 0.3em

  &__debit
    display block
    margin-top 1em

  &__note
    border-radius 0.2em
    margin-bottom 0.5em
    padding-top 0.7em
    padding-bottom @padding-top

  &__content
    flex 1
    min-height 0
    padding-top 1.25em
    padding-bottom 1em
    position relative

  &__spinner
    abs top left right bottom
    background-color inherit
    border-radius inherit
    z-index 3

  &__empty
    display flex
    align-items center
    justify-content center
    width 100%
    height 100%

  &__item
    margin-top 3em
    padding-top @margin-top
    border-top dotted 0.25em $c_dim
    &:first-child
      margin-top 0
      padding-top 0
      border-top 0

  &__items
    height 100%
    &.pretty-scrollbar
      margin-right ($balance_side_padding * -1)
      padding-right 'calc(%s - %s)' % ($balance_side_padding ($scroll_width * 1px))
</style>
